import React, { useState, useRef, useEffect } from 'react';
import Editor from '@monaco-editor/react';

import { submitCodeRun } from '../lib/interviewApi';

// CodingPanel — Monaco-based code editor for live coding interview questions.
//
// Mounted inside InterviewLivePage when the candidate (or the AI) toggles the
// coding panel. Replaces the transcript column. The Run button POSTs the
// current source to /api/interviews/<id>/code-submission, which stores the
// draft and executes it in the sandbox.
//
// Execution engine: **self-hosted Piston**, not Judge0. It runs on its own EC2
// instance with no IAM role, reachable only on port 2000 from the backend's
// private IP, with per-run caps (3s wall clock, 256 MB, 50 KB stdout).
// See backend/integrations/piston_client.py.
//
// (These comments previously said "Judge0" and "the backend returns a stub".
// Both were stale — execution is live via Piston — and were misleading to
// anyone reading the code during technical diligence.)
//
// Starter snippets keep first-render useful for the candidate.

const LANGUAGES = [
  { id: 'python',     label: 'Python 3',         starter: '# Write your solution here\n\ndef solve():\n    pass\n\nsolve()\n' },
  { id: 'javascript', label: 'JavaScript',       starter: '// Write your solution here\n\nfunction solve() {\n  // ...\n}\n\nsolve();\n' },
  { id: 'java',       label: 'Java',             starter: 'public class Main {\n    public static void main(String[] args) {\n        // your code\n    }\n}\n' },
  { id: 'cpp',        label: 'C++ (g++ 17)',     starter: '#include <iostream>\nusing namespace std;\n\nint main() {\n    // your code\n    return 0;\n}\n' },
  { id: 'go',         label: 'Go',               starter: 'package main\n\nimport "fmt"\n\nfunc main() {\n    // your code\n    fmt.Println()\n}\n' },
];

export default function CodingPanel({
  interviewId,
  codingQuestion,   // optional: { questionId, language, prompt, starter } - set when the agent dispatches a code question
  onSubmitAnswer,   // optional: ({language, source_code, questionId}) => Promise - publishes the answer over the data channel
  onClose,
}) {
  const [language, setLanguage] = useState(codingQuestion?.language || LANGUAGES[0].id);
  const [source, setSource] = useState(codingQuestion?.starter || LANGUAGES[0].starter);
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [output, setOutput] = useState(null); // { stdout, stderr, status, time_ms, memory_kb }
  const [error, setError] = useState('');
  const editorRef = useRef(null);

  // When the agent dispatches a NEW coding question (e.g. moving to a
  // second code question in the same interview), reset the editor to
  // its starter so the candidate doesn't see the previous answer.
  useEffect(() => {
    if (!codingQuestion) return;
    setLanguage(codingQuestion.language || LANGUAGES[0].id);
    setSource(codingQuestion.starter || (LANGUAGES.find(
      (l) => l.id === (codingQuestion.language || 'python')) || LANGUAGES[0]).starter);
    setOutput(null);
    setError('');
  }, [codingQuestion?.questionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLanguageChange = (e) => {
    const nextId = e.target.value;
    const next = LANGUAGES.find((l) => l.id === nextId) || LANGUAGES[0];
    setLanguage(next.id);
    // Only reset source if the candidate hasn't written anything yet for
    // this language. Otherwise we'd nuke their in-progress work.
    if (source.trim() === '' || LANGUAGES.some((l) => l.starter.trim() === source.trim())) {
      setSource(next.starter);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    setError('');
    setOutput(null);
    try {
      const result = await submitCodeRun(interviewId, {
        language, source_code: source, action: 'run',
      });
      setOutput(result || {});
    } catch (e) {
      setError(e.message || 'Failed to execute code');
    } finally {
      setRunning(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      await submitCodeRun(interviewId, {
        language, source_code: source, action: 'save',
      });
    } catch (e) {
      // Silent draft-save failure - we'll surface it next Run.
    }
  };

  // Final submission for an agent-dispatched coding question. Persists
  // the answer AND signals the agent over the data channel so it moves
  // on to the next question. Only available when the panel was opened
  // by the agent (codingQuestion + onSubmitAnswer both present).
  const handleSubmitAnswer = async () => {
    if (!codingQuestion || !onSubmitAnswer) return;
    setSubmitting(true);
    try {
      await submitCodeRun(interviewId, { language, source_code: source, action: 'save' });
      await onSubmitAnswer({
        language, source_code: source, questionId: codingQuestion.questionId,
      });
    } catch (e) {
      setError(e.message || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section style={{
      background: 'rgba(0,0,0,0.25)',
      border: '1px solid rgba(253, 250, 240, 0.12)',
      borderRadius: 16,
      padding: 14,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 480,
      gap: 10,
    }}>
      {/* Question prompt (only when the agent dispatched a coding question) */}
      {codingQuestion?.prompt && (
        <div style={{
          padding: '8px 12px',
          background: 'rgba(168, 132, 31, 0.10)',
          border: '1px solid rgba(168, 132, 31, 0.35)',
          borderRadius: 8,
          fontSize: 13,
          color: '#fdfaf0',
          lineHeight: 1.45,
        }}>
          <div style={{
            fontSize: 10, color: '#f0c75e', textTransform: 'uppercase',
            letterSpacing: 1, fontWeight: 600, marginBottom: 4,
          }}>
            Coding question
          </div>
          {codingQuestion.prompt}
        </div>
      )}

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 style={{
          margin: 0, fontSize: 13, color: '#a8841f', flex: 1,
          textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 600,
        }}>
          Coding panel
        </h2>
        <select
          value={language}
          onChange={handleLanguageChange}
          style={{
            background: '#0d1f17', color: '#fdfaf0',
            border: '1px solid rgba(168, 132, 31, 0.45)',
            padding: '4px 8px', borderRadius: 6, fontSize: 12,
          }}
        >
          {LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
        </select>
        <button
          onClick={handleSaveDraft}
          style={{
            background: 'transparent', color: '#fdfaf0',
            border: '1px solid rgba(253, 250, 240, 0.2)',
            padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
          }}
        >
          Save draft
        </button>
        <button
          onClick={handleRun}
          disabled={running}
          style={{
            background: '#a8841f', color: '#1b3a2a',
            border: 0, padding: '4px 14px', borderRadius: 6,
            fontSize: 12, fontWeight: 700, cursor: running ? 'wait' : 'pointer',
            opacity: running ? 0.7 : 1,
          }}
        >
          {running ? 'Running...' : 'Run'}
        </button>
        {codingQuestion && onSubmitAnswer && (
          <button
            onClick={handleSubmitAnswer}
            disabled={submitting}
            style={{
              background: '#9fe5b3', color: '#1b3a2a',
              border: 0, padding: '4px 14px', borderRadius: 6,
              fontSize: 12, fontWeight: 700, cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Submitting...' : 'Submit answer'}
          </button>
        )}
        {onClose && (
          <button
            onClick={onClose}
            title="Hide coding panel"
            style={{
              background: 'transparent', color: 'rgba(253, 250, 240, 0.7)',
              border: '1px solid rgba(253, 250, 240, 0.15)',
              padding: '4px 8px', borderRadius: 6, fontSize: 14, cursor: 'pointer',
            }}
          >
            ×
          </button>
        )}
      </div>

      {/* Editor */}
      <div style={{
        flex: 1, minHeight: 280, borderRadius: 8, overflow: 'hidden',
        border: '1px solid rgba(253, 250, 240, 0.08)',
      }}>
        <Editor
          height="100%"
          language={language}
          value={source}
          onChange={(v) => setSource(v ?? '')}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            scrollBeyondLastLine: false,
            tabSize: 4,
            insertSpaces: true,
            automaticLayout: true,
          }}
          onMount={(editor) => { editorRef.current = editor; }}
        />
      </div>

      {/* Output panel */}
      <div style={{
        background: '#0d1f17',
        border: '1px solid rgba(253, 250, 240, 0.08)',
        borderRadius: 8,
        padding: 10,
        minHeight: 80,
        maxHeight: 180,
        overflowY: 'auto',
        fontFamily: 'Menlo, Monaco, Consolas, monospace',
        fontSize: 12,
        color: '#fdfaf0',
        whiteSpace: 'pre-wrap',
      }}>
        {error ? (
          <span style={{ color: '#ff8a80' }}>{error}</span>
        ) : output ? (
          <>
            {output.stdout && <><span style={{ color: '#9fe5b3' }}>stdout</span>{'\n'}{output.stdout}{'\n'}</>}
            {output.stderr && <><span style={{ color: '#ff8a80' }}>stderr</span>{'\n'}{output.stderr}{'\n'}</>}
            {output.status && (
              <span style={{ color: 'rgba(253, 250, 240, 0.55)' }}>
                {`status: ${output.status}`}
                {output.time_ms != null && `  -  ${output.time_ms} ms`}
                {output.memory_kb != null && `  -  ${output.memory_kb} KB`}
              </span>
            )}
            {!output.stdout && !output.stderr && !output.status && (
              <span style={{ color: 'rgba(253, 250, 240, 0.45)' }}>(no output)</span>
            )}
          </>
        ) : (
          <span style={{ color: 'rgba(253, 250, 240, 0.45)' }}>
            Click Run to execute. Output appears here.
          </span>
        )}
      </div>
    </section>
  );
}
